import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, Button, IconButton, Switch, Chip, List, ListItem, ListItemText, ListItemSecondaryAction, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { accountingAPI } from '../../services/api';
import { errorText } from '../../utils/format';

function CategoryList({ type, title, hint, color, items, reload, notify, onChanged }) {
  const [name, setName] = useState('');
  const [rename, setRename] = useState(null);
  const [newName, setNewName] = useState('');
  const [confirm, setConfirm] = useState(null);

  const add = async () => {
    if (!name.trim()) return;
    try { await accountingAPI.createCategory({ type, name: name.trim() }); setName(''); notify('Category added'); reload(); onChanged(); }
    catch (e) { notify(errorText(e, 'Could not add the category'), 'error'); }
  };
  const toggle = async (c, active) => {
    try { await accountingAPI.updateCategory(c.id, { active }); reload(); onChanged(); }
    catch (e) { notify(errorText(e, 'Could not update'), 'error'); }
  };
  const saveRename = async () => {
    try { await accountingAPI.updateCategory(rename.id, { name: newName }); notify('Renamed. Existing entries were updated too.'); setRename(null); reload(); onChanged(); }
    catch (e) { notify(errorText(e, 'Could not rename'), 'error'); }
  };
  const remove = async () => {
    try { await accountingAPI.deleteCategory(confirm.id); notify('Category deleted'); setConfirm(null); reload(); onChanged(); }
    catch (e) { notify(errorText(e, 'Could not delete'), 'error'); setConfirm(null); }
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #eee', borderLeft: `5px solid ${color}` }}>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{hint}</Typography>
      </Box>
      <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
        <TextField size="small" fullWidth placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={add} sx={{ whiteSpace: 'nowrap' }}>Add</Button>
      </Box>
      <List dense sx={{ pt: 0 }}>
        {items.map((c) => (
          <ListItem key={c.id} divider sx={{ opacity: c.active ? 1 : 0.55 }}>
            <ListItemText primary={c.name} secondary={c.usage ? `Used in ${c.usage} record${c.usage === 1 ? '' : 's'}` : 'Not used yet'} />
            <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center' }}>
              {!c.active && <Chip size="small" label="Off" sx={{ mr: 1 }} />}
              <Switch size="small" checked={c.active} onChange={(e) => toggle(c, e.target.checked)} />
              <IconButton size="small" onClick={() => { setRename(c); setNewName(c.name); }}><EditIcon fontSize="small" /></IconButton>
              <IconButton size="small" color="error" onClick={() => setConfirm(c)}><DeleteIcon fontSize="small" /></IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        {items.length === 0 && <ListItem><ListItemText primary="No categories yet" /></ListItem>}
      </List>

      <Dialog open={!!rename} onClose={() => setRename(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename category</DialogTitle>
        <DialogContent><TextField autoFocus fullWidth size="small" sx={{ mt: 1 }} value={newName} onChange={(e) => setNewName(e.target.value)} /></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setRename(null)}>Cancel</Button><Button variant="contained" onClick={saveRename}>Save</Button></DialogActions>
      </Dialog>
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete category?</DialogTitle>
        <DialogContent><Typography>"{confirm?.name}" will be deleted. Categories that are already used cannot be deleted; switch them off instead.</Typography></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="error" variant="contained" onClick={remove}>Delete</Button></DialogActions>
      </Dialog>
    </Paper>
  );
}

export default function CategoriesTab({ notify, onChanged }) {
  const [income, setIncome] = useState([]);
  const [expense, setExpense] = useState([]);

  const reload = useCallback(async () => {
    try {
      const [i, e] = await Promise.all([accountingAPI.getCategories({ type: 'INCOME' }), accountingAPI.getCategories({ type: 'EXPENSE' })]);
      setIncome(i.data);
      setExpense(e.data);
    } catch (e) { notify(errorText(e, 'Could not load categories'), 'error'); }
  }, [notify]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        You decide which heads appear when income and expenses are recorded. Switch a category off to hide it from new entries without touching old records.
        Renaming a category updates every entry and budget that uses it.
      </Alert>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <CategoryList type="INCOME" title="Income categories" hint="Used for money received other than student fees" color="#00796b" items={income} reload={reload} notify={notify} onChanged={onChanged} />
        </Grid>
        <Grid item xs={12} md={6}>
          <CategoryList type="EXPENSE" title="Expense categories" hint="Used for expenses and for budgets" color="#ef6c00" items={expense} reload={reload} notify={notify} onChanged={onChanged} />
        </Grid>
      </Grid>
    </Box>
  );
}
